import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Watch, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Link2, 
  Unlink, 
  Scale,
  Footprints,
  Loader2,
  AlertCircle,
  Flame,
  Clock,
  ArrowRight,
  ArrowLeft,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

export default function WearableIntegrations({ onBack }) {
  // Fitbit state
  const [fitbitStatus, setFitbitStatus] = useState({ connected: false, loading: true });
  const [fitbitData, setFitbitData] = useState(null);
  const [fitbitSyncing, setFitbitSyncing] = useState(false);
  const [fitbitConnecting, setFitbitConnecting] = useState(false);
  
  // Polar state
  const [polarStatus, setPolarStatus] = useState({ connected: false, loading: true });
  const [polarData, setPolarData] = useState(null);
  const [polarSyncing, setPolarSyncing] = useState(false);
  const [polarConnecting, setPolarConnecting] = useState(false);
  
  // Withings state
  const [withingsStatus, setWithingsStatus] = useState({ connected: false, loading: true });
  const [withingsData, setWithingsData] = useState(null);
  const [withingsSyncing, setWithingsSyncing] = useState(false);
  const [withingsConnecting, setWithingsConnecting] = useState(false);

  // Google Fit state
  const [gfitStatus, setGfitStatus] = useState({ connected: false, loading: true });
  const [gfitData, setGfitData] = useState(null);
  const [gfitSyncing, setGfitSyncing] = useState(false);
  const [gfitConnecting, setGfitConnecting] = useState(false);

  // Sync status
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);

  useEffect(() => {
    checkFitbitStatus();
    checkPolarStatus();
    checkWithingsStatus();
    checkGfitStatus();
    fetchSyncStatus();
    
    // Check for OAuth callback results in URL
    const urlParams = new URLSearchParams(window.location.search);
    
    // Fitbit callbacks
    if (urlParams.get('fitbit_success') === 'true') {
      toast.success('Fitbit connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      checkFitbitStatus();
    } else if (urlParams.get('fitbit_error')) {
      toast.error(`Fitbit connection failed: ${urlParams.get('fitbit_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Polar callbacks
    if (urlParams.get('polar_success') === 'true') {
      toast.success('Polar connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      checkPolarStatus();
    } else if (urlParams.get('polar_error')) {
      toast.error(`Polar connection failed: ${urlParams.get('polar_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Withings callbacks
    if (urlParams.get('withings_success') === 'true') {
      toast.success('Withings connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      checkWithingsStatus();
    } else if (urlParams.get('withings_error')) {
      toast.error(`Withings connection failed: ${urlParams.get('withings_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Google Fit callbacks
    if (urlParams.get('googlefit_success') === 'true') {
      toast.success('Google Fit connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      checkGfitStatus();
    } else if (urlParams.get('googlefit_error')) {
      toast.error(`Google Fit connection failed: ${urlParams.get('googlefit_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const status = await api.get('/wearables/sync-status');
      setSyncStatus(status);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const syncAllWearables = async () => {
    setSyncingAll(true);
    try {
      const results = await api.post('/wearables/sync-all');
      let successCount = 0;
      let messages = [];
      
      if (results.fitbit?.synced) {
        successCount++;
        messages.push(`Fitbit: ${results.fitbit.data?.steps_today || 0} steps, ${results.fitbit.data?.weight_kg || '--'} kg`);
      }
      if (results.polar?.synced) {
        successCount++;
        messages.push(`Polar: ${results.polar.data?.calories_added || 0} kcal`);
      }
      if (results.withings?.synced) {
        successCount++;
        messages.push(`Withings: ${results.withings.data?.weight_kg || '--'} kg`);
      }
      
      if (successCount > 0) {
        toast.success(`Synced ${successCount} service(s): ${messages.join(', ')}`);
      } else {
        toast.info('No wearables connected to sync');
      }
      
      fetchSyncStatus();
      if (fitbitStatus.connected) fetchFitbitData();
      if (polarStatus.connected) fetchPolarData();
      if (withingsStatus.connected) fetchWithingsData();
    } catch (error) {
      toast.error('Failed to sync wearables');
    } finally {
      setSyncingAll(false);
    }
  };

  // ==================== FITBIT FUNCTIONS ====================
  const checkFitbitStatus = async () => {
    try {
      const status = await api.get('/wearables/fitbit/status');
      setFitbitStatus({ ...status, loading: false });
      if (status.connected) fetchFitbitData();
    } catch (error) {
      setFitbitStatus({ connected: false, loading: false });
    }
  };

  const fetchFitbitData = async () => {
    try {
      const data = await api.get('/wearables/fitbit/data');
      setFitbitData(data);
    } catch (error) {
      console.error('Error fetching Fitbit data:', error);
    }
  };

  const connectFitbit = async () => {
    setFitbitConnecting(true);
    try {
      const { auth_url } = await api.get('/wearables/fitbit/auth-url');
      window.location.href = auth_url;
    } catch (error) {
      toast.error('Failed to start Fitbit connection');
      setFitbitConnecting(false);
    }
  };

  const disconnectFitbit = async () => {
    try {
      await api.delete('/wearables/fitbit/disconnect');
      setFitbitStatus({ connected: false, loading: false });
      setFitbitData(null);
      toast.success('Fitbit disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Fitbit');
    }
  };

  const syncFitbit = async () => {
    setFitbitSyncing(true);
    try {
      const result = await api.post('/wearables/fitbit/sync');
      setFitbitData({ has_data: true, ...result.data, last_synced: new Date().toISOString() });
      toast.success(`Synced: ${result.synced.steps_days} days of steps, ${result.synced.weight_entries} weight entries`);
      fetchSyncStatus();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Fitbit authorization expired. Please reconnect.');
        setFitbitStatus({ connected: false, loading: false });
      } else {
        toast.error('Failed to sync Fitbit data');
      }
    } finally {
      setFitbitSyncing(false);
    }
  };

  // ==================== POLAR FUNCTIONS ====================
  const checkPolarStatus = async () => {
    try {
      const status = await api.get('/wearables/polar/status');
      setPolarStatus({ ...status, loading: false });
      if (status.connected) fetchPolarData();
    } catch (error) {
      setPolarStatus({ connected: false, loading: false });
    }
  };

  const fetchPolarData = async () => {
    try {
      const data = await api.get('/wearables/polar/data');
      setPolarData(data);
    } catch (error) {
      console.error('Error fetching Polar data:', error);
    }
  };

  const connectPolar = async () => {
    setPolarConnecting(true);
    try {
      const { auth_url } = await api.get('/wearables/polar/auth-url');
      window.location.href = auth_url;
    } catch (error) {
      toast.error('Failed to start Polar connection');
      setPolarConnecting(false);
    }
  };

  const disconnectPolar = async () => {
    try {
      await api.delete('/wearables/polar/disconnect');
      setPolarStatus({ connected: false, loading: false });
      setPolarData(null);
      toast.success('Polar disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Polar');
    }
  };

  const syncPolar = async () => {
    setPolarSyncing(true);
    try {
      const result = await api.post('/wearables/polar/sync');
      setPolarData({ has_data: true, ...result.data, last_synced: new Date().toISOString() });
      toast.success(`Synced: ${result.synced.exercises} exercises, ${result.synced.total_calories} kcal burned`);
      fetchSyncStatus();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Polar authorization expired. Please reconnect.');
        setPolarStatus({ connected: false, loading: false });
      } else {
        toast.error('Failed to sync Polar data');
      }
    } finally {
      setPolarSyncing(false);
    }
  };

  const migratePolarCalories = async () => {
    setPolarSyncing(true);
    try {
      const result = await api.post('/wearables/polar/migrate-calories');
      if (result.success && result.total_calories > 0) {
        toast.success(`✅ Migrated ${result.total_calories} kcal to Nutritional Tracker!`);
        fetchSyncStatus();
      } else {
        toast.info('No new calories to migrate');
      }
    } catch (error) {
      toast.error('Failed to migrate calories');
    } finally {
      setPolarSyncing(false);
    }
  };

  // ==================== WITHINGS FUNCTIONS ====================
  const checkWithingsStatus = async () => {
    try {
      const status = await api.get('/wearables/withings/status');
      setWithingsStatus({ ...status, loading: false });
      if (status.connected) fetchWithingsData();
    } catch (error) {
      setWithingsStatus({ connected: false, loading: false });
    }
  };

  const fetchWithingsData = async () => {
    try {
      const data = await api.get('/wearables/withings/data');
      setWithingsData(data);
    } catch (error) {
      console.error('Error fetching Withings data:', error);
    }
  };

  const connectWithings = async () => {
    setWithingsConnecting(true);
    try {
      const { auth_url } = await api.get('/wearables/withings/auth-url');
      window.location.href = auth_url;
    } catch (error) {
      toast.error('Failed to start Withings connection');
      setWithingsConnecting(false);
    }
  };

  const disconnectWithings = async () => {
    try {
      await api.delete('/wearables/withings/disconnect');
      setWithingsStatus({ connected: false, loading: false });
      setWithingsData(null);
      toast.success('Withings disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Withings');
    }
  };

  const syncWithings = async () => {
    setWithingsSyncing(true);
    try {
      const result = await api.post('/wearables/withings/sync');
      setWithingsData({ has_data: true, ...result.data, last_synced: new Date().toISOString() });
      toast.success(`Synced: ${result.synced.weight_entries} weight entries`);
      fetchSyncStatus();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Withings authorization expired. Please reconnect.');
        setWithingsStatus({ connected: false, loading: false });
      } else {
        toast.error('Failed to sync Withings data');
      }
    } finally {
      setWithingsSyncing(false);
    }
  };

  // ==================== DATA HELPERS ====================

  // Google Fit functions
  const checkGfitStatus = async () => {
    try {
      const status = await api.get('/wearables/googlefit/status');
      setGfitStatus({ connected: status.connected, loading: false, connected_at: status.connected_at });
      if (status.connected) {
        const data = await api.get('/wearables/googlefit/data');
        setGfitData(data);
      }
    } catch (err) { setGfitStatus({ connected: false, loading: false }); }
  };

  const connectGfit = async () => {
    setGfitConnecting(true);
    try {
      const response = await api.get('/wearables/googlefit/auth-url');
      window.location.href = response.auth_url;
    } catch (err) {
      toast.error('Failed to connect Google Fit');
      setGfitConnecting(false);
    }
  };

  const disconnectGfit = async () => {
    try {
      await api.delete('/wearables/googlefit/disconnect');
      setGfitStatus({ connected: false, loading: false });
      setGfitData(null);
      toast.success('Google Fit disconnected');
    } catch (err) { toast.error('Failed to disconnect'); }
  };

  const syncGfit = async () => {
    setGfitSyncing(true);
    try {
      const result = await api.post('/wearables/googlefit/sync');
      setGfitData({ steps: result.steps, calories: result.calories });
      toast.success('Google Fit synced!');
    } catch (err) { toast.error('Failed to sync Google Fit'); }
    finally { setGfitSyncing(false); }
  };

  const getGfitTodaySteps = () => {
    if (!gfitData?.steps?.length) return null;
    const today = new Date().toISOString().split('T')[0];
    const todayData = gfitData.steps.find(s => s.date === today);
    return todayData ? todayData.steps : null;
  };

  const getGfitTodayCalories = () => {
    if (!gfitData?.calories?.length) return null;
    const today = new Date().toISOString().split('T')[0];
    const todayData = gfitData.calories.find(s => s.date === today);
    return todayData ? todayData.calories : null;
  };

  const getTodaySteps = () => {
    if (!fitbitData?.steps?.length) return null;
    const today = new Date().toISOString().split('T')[0];
    const todayData = fitbitData.steps.find(s => s.dateTime === today);
    return todayData ? parseInt(todayData.value) : null;
  };

  const getFitbitWeight = () => {
    if (!fitbitData?.weight?.length) return null;
    const sorted = [...fitbitData.weight].sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
    return sorted[0] ? parseFloat(sorted[0].value) : null;
  };

  const getPolarCalories = () => {
    if (!polarData?.exercises?.length) return 0;
    return polarData.exercises.reduce((sum, ex) => sum + (ex.calories || ex['energy-expended'] || 0), 0);
  };

  const getWithingsWeight = () => {
    if (!withingsData?.weight?.length) return null;
    const sorted = [...withingsData.weight].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted[0] || null;
  };

  const todaySteps = getTodaySteps();
  const fitbitWeight = getFitbitWeight();
  const polarCalories = getPolarCalories();
  const withingsWeight = getWithingsWeight();

  const hasAnyConnection = fitbitStatus.connected || polarStatus.connected || withingsStatus.connected || gfitStatus.connected;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-none ios-header" style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))', border: 'none' }} data-header="true">
        <div className="max-w-lg mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={onBack} className="p-2 -ml-2 rounded-xl hover:bg-slate-800 transition-colors" data-testid="wearables-back-btn">
            <ArrowLeft className="h-5 w-5 text-slate-300" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Watch className="h-5 w-5 text-cyan-400" />
              Wearable Devices
            </h1>
            <p className="text-slate-400 text-xs">Auto-sync your fitness data</p>
          </div>
          {hasAnyConnection && (
            <Button 
              onClick={syncAllWearables} 
              disabled={syncingAll}
              size="sm"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
            >
              {syncingAll ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
              Sync All
            </Button>
          )}
        </div>
      </header>
      <div className="max-w-lg mx-auto space-y-6 p-4 pb-24">

        {/* Data Integration Info */}
        <Card className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="h-5 w-5 text-cyan-400" />
              <span className="font-semibold text-white">Auto-Sync Schedule</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <Footprints className="h-4 w-4 text-green-400" />
                <span>Steps → Nutrition Tracker</span>
                <span className="text-xs text-slate-500">(6x/day)</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Scale className="h-4 w-4 text-blue-400" />
                <span>Weight → Body Info</span>
                <span className="text-xs text-slate-500">(daily)</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Flame className="h-4 w-4 text-orange-400" />
                <span>Calories → Burned kcal</span>
                <span className="text-xs text-slate-500">(4x/day)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Integrated Data */}
        {syncStatus?.integrated_data && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-slate-400" />
                Today's Synced Data
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                  <Footprints className="h-5 w-5 text-green-400 mx-auto mb-1" />
                  <div className="text-xl font-bold text-white">
                    {syncStatus.integrated_data.external_steps?.value?.toLocaleString() || '--'}
                  </div>
                  <div className="text-xs text-slate-400">External Steps</div>
                  {syncStatus.integrated_data.external_steps?.source && syncStatus.integrated_data.external_steps.source !== 'manual' && (
                    <div className="text-xs text-cyan-400 mt-1">via {syncStatus.integrated_data.external_steps.source}</div>
                  )}
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                  <Scale className="h-5 w-5 text-blue-400 mx-auto mb-1" />
                  <div className="text-xl font-bold text-white">
                    {syncStatus.integrated_data.bodyweight?.value || '--'} {syncStatus.integrated_data.bodyweight?.unit || ''}
                  </div>
                  <div className="text-xs text-slate-400">Body Weight</div>
                  {syncStatus.integrated_data.bodyweight?.source && syncStatus.integrated_data.bodyweight.source !== 'manual' && (
                    <div className="text-xs text-cyan-400 mt-1">via {syncStatus.integrated_data.bodyweight.source}</div>
                  )}
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                  <Flame className="h-5 w-5 text-orange-400 mx-auto mb-1" />
                  <div className="text-xl font-bold text-white">
                    {syncStatus.integrated_data.burned_calories?.value?.toLocaleString() || '--'}
                  </div>
                  <div className="text-xs text-slate-400">Burned kcal</div>
                  {syncStatus.integrated_data.burned_calories?.source && syncStatus.integrated_data.burned_calories.source !== 'manual' && (
                    <div className="text-xs text-cyan-400 mt-1">via {syncStatus.integrated_data.burned_calories.source}</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fitbit Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#00B0B9] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">f</span>
                </div>
                <div>
                  <span>Fitbit</span>
                  {fitbitStatus.connected && <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Connected</span>}
                </div>
              </div>
              {fitbitStatus.loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : 
               fitbitStatus.connected ? <CheckCircle className="h-5 w-5 text-green-400" /> : <XCircle className="h-5 w-5 text-slate-500" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!fitbitStatus.connected ? (
              <div className="text-center py-4">
                <p className="text-slate-400 mb-2">Sync steps and weight from your Fitbit device.</p>
                <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-4">
                  <Footprints className="h-4 w-4" /> Steps <ArrowRight className="h-3 w-3" /> Nutrition Tracker
                  <span className="mx-2">|</span>
                  <Scale className="h-4 w-4" /> Weight <ArrowRight className="h-3 w-3" /> Body Info
                </div>
                <Button onClick={connectFitbit} disabled={fitbitConnecting} className="bg-[#00B0B9] hover:bg-[#009BA3] text-white">
                  {fitbitConnecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</> : <><Link2 className="h-4 w-4 mr-2" />Connect Fitbit</>}
                </Button>
              </div>
            ) : (
              <>
                {fitbitData?.has_data && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1"><Footprints className="h-4 w-4" />Today's Steps</div>
                      <div className="text-2xl font-bold text-white">{todaySteps?.toLocaleString() || '--'}</div>
                      <div className="text-xs text-green-400 mt-1">→ External Steps</div>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1"><Scale className="h-4 w-4" />Latest Weight</div>
                      <div className="text-2xl font-bold text-white">{fitbitWeight ? `${fitbitWeight.toFixed(1)} kg` : '--'}</div>
                      <div className="text-xs text-blue-400 mt-1">→ Body Info</div>
                    </div>
                  </div>
                )}
                {fitbitData?.last_synced && <p className="text-xs text-slate-500 text-center">Last synced: {new Date(fitbitData.last_synced).toLocaleString()}</p>}
                <div className="flex gap-2">
                  <Button onClick={syncFitbit} disabled={fitbitSyncing} className="flex-1 bg-[#00B0B9] hover:bg-[#009BA3]">
                    {fitbitSyncing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Syncing...</> : <><RefreshCw className="h-4 w-4 mr-2" />Sync</>}
                  </Button>
                  <Button onClick={disconnectFitbit} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <Unlink className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Polar Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#D70040] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">P</span>
                </div>
                <div>
                  <span>Polar</span>
                  {polarStatus.connected && <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Connected</span>}
                </div>
              </div>
              {polarStatus.loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : 
               polarStatus.connected ? <CheckCircle className="h-5 w-5 text-green-400" /> : <XCircle className="h-5 w-5 text-slate-500" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!polarStatus.connected ? (
              <div className="text-center py-4">
                <p className="text-slate-400 mb-2">Sync workout calories from your Polar device.</p>
                <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-4">
                  <Flame className="h-4 w-4" /> Workout Calories <ArrowRight className="h-3 w-3" /> Burned kcal
                </div>
                <Button onClick={connectPolar} disabled={polarConnecting} className="bg-[#D70040] hover:bg-[#B8003A] text-white">
                  {polarConnecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</> : <><Link2 className="h-4 w-4 mr-2" />Connect Polar</>}
                </Button>
              </div>
            ) : (
              <>
                {polarData?.has_data && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1"><Flame className="h-4 w-4 text-orange-400" />Total Calories</div>
                      <div className="text-2xl font-bold text-white">{polarCalories.toLocaleString()} kcal</div>
                      <div className="text-xs text-orange-400 mt-1">→ Burned kcal</div>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-1"><Clock className="h-4 w-4" />Workouts</div>
                      <div className="text-2xl font-bold text-white">{polarData?.exercises?.length || 0}</div>
                      <div className="text-xs text-slate-400 mt-1">sessions synced</div>
                    </div>
                  </div>
                )}
                {polarData?.last_synced && <p className="text-xs text-slate-500 text-center">Last synced: {new Date(polarData.last_synced).toLocaleString()}</p>}
                
                {/* Show migrate button if there's Polar data but it might not be in Nutrition Tracker */}
                {polarData?.has_data && polarCalories > 0 && (
                  <Button 
                    onClick={migratePolarCalories} 
                    disabled={polarSyncing}
                    variant="outline"
                    className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/10 mb-2"
                  >
                    <Flame className="h-4 w-4 mr-2" />
                    Send {polarCalories} kcal to Nutrition Tracker
                  </Button>
                )}
                
                <div className="flex gap-2">
                  <Button onClick={syncPolar} disabled={polarSyncing} className="flex-1 bg-[#D70040] hover:bg-[#B8003A]">
                    {polarSyncing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Syncing...</> : <><RefreshCw className="h-4 w-4 mr-2" />Sync</>}
                  </Button>
                  <Button onClick={disconnectPolar} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <Unlink className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Withings Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#00A1E4] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">W</span>
                </div>
                <div>
                  <span>Withings</span>
                  {withingsStatus.connected && <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Connected</span>}
                </div>
              </div>
              {withingsStatus.loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : 
               withingsStatus.connected ? <CheckCircle className="h-5 w-5 text-green-400" /> : <XCircle className="h-5 w-5 text-slate-500" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!withingsStatus.connected ? (
              <div className="text-center py-4">
                <p className="text-slate-400 mb-2">Sync weight from your Withings smart scale.</p>
                <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-4">
                  <Scale className="h-4 w-4" /> Weight <ArrowRight className="h-3 w-3" /> Body Info
                </div>
                <Button onClick={connectWithings} disabled={withingsConnecting} className="bg-[#00A1E4] hover:bg-[#0091D4] text-white">
                  {withingsConnecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</> : <><Link2 className="h-4 w-4 mr-2" />Connect Withings</>}
                </Button>
              </div>
            ) : (
              <>
                {withingsData?.has_data && (
                  <div className="bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1"><Scale className="h-4 w-4" />Latest Weight</div>
                    <div className="text-2xl font-bold text-white">{withingsWeight?.weight ? `${withingsWeight.weight} kg` : '--'}</div>
                    <div className="text-xs text-blue-400 mt-1">→ Body Info</div>
                  </div>
                )}
                {withingsData?.last_synced && <p className="text-xs text-slate-500 text-center">Last synced: {new Date(withingsData.last_synced).toLocaleString()}</p>}
                <div className="flex gap-2">
                  <Button onClick={syncWithings} disabled={withingsSyncing} className="flex-1 bg-[#00A1E4] hover:bg-[#0091D4]">
                    {withingsSyncing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Syncing...</> : <><RefreshCw className="h-4 w-4 mr-2" />Sync</>}
                  </Button>
                  <Button onClick={disconnectWithings} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <Unlink className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Google Fit Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#4285F4] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">G</span>
                </div>
                <div>
                  <span>Google Fit</span>
                  <p className="text-xs text-slate-400 font-normal">Steps & Calories</p>
                </div>
              </div>
              {gfitStatus.loading ? (
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              ) : gfitStatus.connected ? (
                <CheckCircle className="h-5 w-5 text-green-400" />
              ) : (
                <XCircle className="h-5 w-5 text-slate-500" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!gfitStatus.connected ? (
              <Button onClick={connectGfit} disabled={gfitConnecting} className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white">
                {gfitConnecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</> : <><Link2 className="h-4 w-4 mr-2" />Connect Google Fit</>}
              </Button>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                    <Footprints className="h-4 w-4 text-green-400 mx-auto mb-1" />
                    <div className="text-lg font-bold text-white">{getGfitTodaySteps()?.toLocaleString() || '--'}</div>
                    <div className="text-xs text-slate-400">Steps Today</div>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                    <Flame className="h-4 w-4 text-orange-400 mx-auto mb-1" />
                    <div className="text-lg font-bold text-white">{getGfitTodayCalories()?.toLocaleString() || '--'}</div>
                    <div className="text-xs text-slate-400">Calories</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={syncGfit} disabled={gfitSyncing} className="flex-1 bg-[#4285F4] hover:bg-[#3367D6]">
                    {gfitSyncing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Syncing...</> : <><RefreshCw className="h-4 w-4 mr-2" />Sync</>}
                  </Button>
                  <Button onClick={disconnectGfit} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <Unlink className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-200">
              <p className="font-medium mb-1">Automatic Data Integration</p>
              <p className="text-blue-300/80">
                Connected wearables automatically sync to your app data. Steps appear in Nutrition Tracker's "External steps", 
                weight updates your Body Info, and workout calories add to "Burned kcal".
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
